import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { LockIcon } from 'lucide-react';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { AccountAutoComplete, StorageLocationAutoComplete } from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { MassEditWizard } from '@/components/common/mass-edit-wizard';
import { ShipmentAutoComplete } from '@/components/common/shipment-auto-complete';
import { StorageLocationCombobox } from '@/components/common/storage-location-combobox';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button as UiButton } from '@/components/ui/button';
import {
  DATE_PICKER_COMPACT,
  DatePickerInput,
} from '@/components/ui/date-picker-input';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EXPORT_ENUMS, SEX } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Sire } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import { useEditSemen, useEditSemenV2 } from '@/services/semen-service';
import { EditSemenInput } from '@/services/semen-service/types';
import { SemenDto } from '@/services/semen-service/types';
import {
  cssCertifiedOptions,
  SemenTypeOptions,
  sexedOptions,
} from '@/types/common';

import { createEditSemenFormSchema } from './schema';
import {
  EditSemenFormInput,
  EditSemenFormProps,
  EditSemenFormValues,
} from './types';
import { massEditSemenConfig } from './wizard-config';

const CONVENTIONAL_SEMEN_TYPE = 'Conv';

export const EditSemenForm = ({
  visible = true,
  onCancel,
  semen,
  rows,
  onSuccess,
  onError,
  calendarContainer,
}: EditSemenFormProps) => {
  const dispatch = useDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const [selectedSemen, setSelectedSemen] = useState<SemenDto | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<EditSemenFormInput | null>(null);
  const isBlockInactiveSiresEnabled = useFeatureFlag('block_inactive_sires');
  const isBaseUi = useFeatureFlag('edit_semen_prime_to_base_ui');
  const { mutateAsync: editSemen } = useEditSemen();
  const { mutateAsync: editSemenV2 } = useEditSemenV2();
  const isSemenDataPointsEnabled = useFeatureFlag('Semen_Data_Points');

  const isBulkEdit = rows && rows.length > 1;
  const currentSemen = selectedSemen || semen;
  const isSemenTypeLabelEnabled = useFeatureFlag('Semen_Type_Field_Label');
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const isConditionalValidation = useFeatureFlag(
    'Semen_Conditional_Validation',
  );

  const editSemenFormSchema = useMemo(
    () => createEditSemenFormSchema(isConditionalValidation),
    [isConditionalValidation],
  );

  const derivedMassEditSemenConfig = useMemo(() => {
    if (!isSemenTypeLabelEnabled) return massEditSemenConfig;
    const { semenType: _semenType, ...restFields } = massEditSemenConfig.fields;
    return {
      ...massEditSemenConfig,
      fields: {
        ...restFields,
        sex: { ...massEditSemenConfig.fields.sex, label: 'Semen Type' },
      },
    };
  }, [isSemenTypeLabelEnabled]);

  useEffect(() => {
    if (semen) {
      setSelectedSemen(null);
    }
  }, [semen]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<EditSemenFormInput>({
    resolver: zodResolver(editSemenFormSchema),
    defaultValues: {
      semenId: semen?.id || '',
      dateReceived: semen?.dateReceived
        ? new Date(semen.dateReceived)
        : new Date(),
      sire: semen?.sire ? (semen.sire as SemenDto['sire']) : undefined,
      owner:
        semen?.ownerId && semen?.ownerName
          ? {
              id: semen.ownerId,
              name: semen.ownerName,
              code: semen.ownerCode || '',
            }
          : undefined,
      colCode: semen?.colCode || '',
      storageLocation: semen?.storageLocation
        ? {
            id: semen.storageLocation.id,
            name: semen.storageLocation.name,
          }
        : undefined,
      quantityReceived:
        semen?.quantityReceived !== undefined &&
        semen?.quantityReceived !== null
          ? parseInt(String(semen.quantityReceived), 10)
          : undefined,
      cane: semen?.cane || '',
      naab: semen?.naab || '',
      sex: semen?.sex
        ? typeof semen.sex === 'string'
          ? parseInt(semen.sex, 10) || undefined
          : semen.sex
        : undefined,
      sexed: isSemenTypeLabelEnabled
        ? undefined
        : semen?.sexed === true
          ? 'Yes'
          : 'No',
      cssCertified:
        semen?.cssCertified !== undefined ? semen.cssCertified : undefined,
      export: semen?.export !== undefined ? semen.export : undefined,
      center: semen?.center || undefined,
      shortName: semen?.sire?.name || '',
      quantity:
        semen?.quantity !== undefined && semen?.quantity !== null
          ? parseInt(String(semen.quantity), 10)
          : undefined,
      quantityToShip:
        semen?.quantityToShip !== undefined && semen?.quantityToShip !== null
          ? parseInt(String(semen.quantityToShip), 10)
          : undefined,
      soldTo: (semen as any)?.soldTo || undefined,
      sortStatus: (semen as any)?.sortStatus || undefined,
      comment: (semen as any)?.comment || (semen as any)?.comments || undefined,
      critical: (semen as any)?.critical || undefined,
      storageStartDate: (semen as any)?.storageStartDate
        ? new Date((semen as any).storageStartDate)
        : undefined,
      secondaryOwner: (semen as any)?.secondaryOwner || undefined,
      cost:
        (semen as any)?.cost !== undefined && (semen as any)?.cost !== null
          ? typeof (semen as any).cost === 'string'
            ? parseFloat((semen as any).cost)
            : (semen as any).cost
          : undefined,
      shipment: (semen as any)?.shipmentId
        ? {
            id: (semen as any).shipmentId,
            shipmentNumber: (semen as any)?.shipmentNumber || undefined,
            shipName: (semen as any)?.shipmentName || undefined,
          }
        : undefined,
      semenType: isSemenTypeLabelEnabled
        ? undefined
        : semen?.semenType || undefined,
      units:
        semen?.units !== undefined && semen?.units !== null
          ? semen.units
          : undefined,
      country: semen?.country || undefined,
    } as EditSemenFormInput,
  });

  const watchedSire = watch('sire');
  const watchedExport = watch('export') as EditSemenFormInput['export'];
  const watchedSex = watch('sex') as EditSemenFormInput['sex'];
  const watchedStorageLocation = watch('storageLocation') as
    | { id: string; name: string }
    | undefined;

  const isNaabRequired =
    isConditionalValidation &&
    (watchedSex === SEX.Male || watchedSex === SEX.Female);

  useEffect(() => {
    if (visible && semen) {
      reset({
        semenId: semen.id || '',
        dateReceived: semen.dateReceived
          ? new Date(semen.dateReceived)
          : new Date(),
        sire: semen.sire ? (semen.sire as Sire) : undefined,
        owner:
          semen.ownerId && semen.ownerName
            ? {
                id: semen.ownerId,
                name: semen.ownerName,
                code: semen.ownerCode || '',
              }
            : undefined,
        colCode: semen.colCode || '',
        storageLocation: semen.storageLocation
          ? {
              id: semen.storageLocation.id,
              name: semen.storageLocation.name,
            }
          : undefined,
        quantityReceived:
          semen.quantityReceived !== undefined &&
          semen.quantityReceived !== null
            ? parseInt(String(semen.quantityReceived), 10)
            : undefined,
        cane: semen.cane || '',
        naab: semen.naab || '',
        sex: semen.sex
          ? typeof semen.sex === 'string'
            ? parseInt(semen.sex, 10) || undefined
            : semen.sex
          : undefined,
        sexed: isSemenTypeLabelEnabled
          ? undefined
          : semen.sexed === true
            ? 'Yes'
            : 'No',
        cssCertified:
          semen.cssCertified !== undefined ? semen.cssCertified : undefined,
        export: semen.export !== undefined ? semen.export : undefined,
        center: semen.center || undefined,
        shortName: semen.sire?.name || '',
        quantity:
          semen.quantity !== undefined && semen.quantity !== null
            ? parseInt(String(semen.quantity), 10)
            : undefined,
        quantityToShip:
          semen.quantityToShip !== undefined && semen.quantityToShip !== null
            ? parseInt(String(semen.quantityToShip), 10)
            : undefined,
        soldTo: (semen as any)?.soldTo || undefined,
        sortStatus: (semen as any)?.sortStatus || undefined,
        comment:
          (semen as any)?.comment || (semen as any)?.comments || undefined,
        critical: (semen as any)?.critical || undefined,
        storageStartDate: (semen as any)?.storageStartDate
          ? new Date((semen as any).storageStartDate)
          : undefined,
        secondaryOwner: (semen as any)?.secondaryOwner || undefined,
        cost:
          (semen as any)?.cost !== undefined && (semen as any)?.cost !== null
            ? typeof (semen as any).cost === 'string'
              ? parseFloat((semen as any).cost)
              : (semen as any).cost
            : undefined,
        shipment: (semen as any)?.shipmentId
          ? {
              id: (semen as any).shipmentId,
              shipmentNumber: (semen as any)?.shipmentNumber || undefined,
              shipName: (semen as any)?.shipmentName || undefined,
            }
          : undefined,
        semenType: isSemenTypeLabelEnabled
          ? undefined
          : semen?.semenType || undefined,
        units:
          semen?.units !== undefined && semen?.units !== null
            ? semen.units
            : undefined,
        country: semen?.country || undefined,
      } as EditSemenFormInput);
    }
  }, [visible, semen, reset, isSemenTypeLabelEnabled]);

  useEffect(() => {
    if (
      watchedSire &&
      typeof watchedSire === 'object' &&
      'name' in watchedSire
    ) {
      const sireName = (watchedSire as { name: string }).name;
      if (sireName) {
        setValue('shortName' as keyof EditSemenFormInput, sireName);
      }
    }
  }, [watchedSire, setValue]);

  useEffect(() => {
    if (!isBulkEdit) return;

    const subscription = watch((values) => {
      const hasValues = Object.entries(values).some(([key, value]) => {
        if (key === 'semenId') return false;
        if (value === undefined || value === null) return false;
        if (value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (typeof value === 'object' && Object.keys(value).length === 0)
          return false;
        return true;
      });

      if (hasValues) {
        setFormValues(values as EditSemenFormInput);
      } else {
        setFormValues(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, isBulkEdit]);

  const getExportStatusLabel = (
    exportValue: number | undefined | null,
  ): string | null => {
    if (exportValue === undefined || exportValue === null) {
      return null;
    }
    const entry = Object.entries(EXPORT_ENUMS).find(
      ([, value]) => value === exportValue,
    );
    return entry ? entry[0] : null;
  };

  const isStorageLocationQualified = useMemo(() => {
    if (
      watchedExport === undefined ||
      watchedExport === null ||
      watchedExport === 0
    ) {
      return true;
    }
    if (!watchedStorageLocation) {
      return true;
    }
    const exportLabel = getExportStatusLabel(
      watchedExport as number | undefined,
    );
    if (!exportLabel) {
      return true;
    }
    const storageLocation = watchedStorageLocation as { name?: string };
    const storageLocationName = storageLocation.name || '';
    return storageLocationName
      .toLowerCase()
      .includes(exportLabel.toLowerCase());
  }, [watchedExport, watchedStorageLocation]);

  const storageLocationTooltip = useMemo(() => {
    if (
      watchedExport === undefined ||
      watchedExport === null ||
      watchedExport === 0
    ) {
      return '';
    }
    if (!watchedStorageLocation) {
      return '';
    }
    const exportLabel = getExportStatusLabel(
      watchedExport as number | undefined,
    );
    if (!exportLabel) {
      return '';
    }
    const storageLocation = watchedStorageLocation as { name?: string };
    const storageLocationName = storageLocation.name || '';
    if (
      !storageLocationName.toLowerCase().includes(exportLabel.toLowerCase())
    ) {
      return `Warning: The selected storage location "${storageLocationName}" does not include the export status "${exportLabel}".`;
    }
    return '';
  }, [watchedExport, watchedStorageLocation]);

  const handleStorageLocationSelect = useCallback(
    (value: unknown) => {
      if (
        watchedExport !== undefined &&
        watchedExport !== null &&
        watchedExport !== 0
      ) {
        const exportLabel = getExportStatusLabel(
          watchedExport as number | undefined,
        );

        if (exportLabel && value) {
          const storageLocation = value as { name?: string };
          const storageLocationName = storageLocation.name || '';

          if (
            !storageLocationName
              .toLowerCase()
              .includes(exportLabel.toLowerCase())
          ) {
            dispatch(
              addToast({
                severity: 'warn',
                summary: t('storageLocation.storageLocationWarning'),
                detail: t('storageLocation.storageLocationMismatch', {
                  location: storageLocationName,
                  status: exportLabel,
                }),
                life: ToastLife.MEDIUM,
              }),
            );
          }
        }
      }
    },
    [watchedExport, dispatch],
  );

  const getFormErrorMessage = (name: keyof EditSemenFormInput) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const transformFormDataToApiInput = (
    data: EditSemenFormInput | EditSemenFormValues,
    semenId: string,
  ): EditSemenInput => {
    const transformedData = editSemenFormSchema.parse(data);
    const formInput = data as EditSemenFormInput;

    return {
      semenId,
      cane: transformedData.cane || '',
      ownerId:
        typeof formInput.owner === 'string'
          ? formInput.owner
          : formInput.owner?.id,
      storageLocationId:
        typeof formInput.storageLocation === 'string'
          ? formInput.storageLocation
          : formInput.storageLocation?.id,
      sireId: formInput.sire?.id,
      ...(isSemenTypeLabelEnabled
        ? {}
        : { sexed: transformedData.sexed === 'Yes' }),
      sex: transformedData.sex,
      cssCertified: transformedData.cssCertified ?? undefined,
      dateReceived:
        transformedData.dateReceived?.toISOString() || new Date().toISOString(),
      colCode: transformedData.colCode,
      naab: transformedData.naab,
      quantityRec: transformedData.quantityReceived || 0,
      quantity: transformedData.quantity,
      quantityToShip: transformedData.quantityToShip,
      center: transformedData.center,
      export: transformedData.export ?? undefined,
      shortName: transformedData.shortName,
      soldTo: transformedData.soldTo,
      sortStatus: transformedData.sortStatus,
      comment: transformedData.comment,
      critical: transformedData.critical,
      storageStartDate: transformedData.storageStartDate?.toISOString(),
      secondaryOwner: transformedData.secondaryOwner,
      cost: transformedData.cost,
      shipmentId:
        typeof transformedData.shipment === 'string'
          ? transformedData.shipment
          : transformedData.shipment?.id,
      semenType:
        isSemenConventionalTypeEnabled && transformedData.sex === SEX.Unknown
          ? CONVENTIONAL_SEMEN_TYPE
          : isSemenTypeLabelEnabled
            ? undefined
            : transformedData.semenType,
      units: transformedData.units,
      country: transformedData.country,
    };
  };

  const handleApiResults = (
    results: Array<{ success: boolean; message?: string }>,
    successMessage: string,
  ) => {
    const failedResults = results.filter((r) => r.success === false);

    if (failedResults.length > 0) {
      failedResults.forEach((result) => {
        dispatch(
          addToast({
            severity: 'warn',
            summary: 'Error',
            detail: result.message || 'Failed to update semen',
            life: ToastLife.MEDIUM,
          }),
        );
      });
    } else {
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Success',
          detail: successMessage,
          life: ToastLife.MEDIUM,
        }),
      );
    }
  };

  const handleFormSubmit = async (data: EditSemenFormInput) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      const semenId =
        data.semenId && data.semenId.trim() !== ''
          ? data.semenId
          : currentSemen?.id || semen?.id;
      if (!semenId) {
        throw new Error('Semen ID is required');
      }
      const validatedData = editSemenFormSchema.parse(data);
      const transformedInput = transformFormDataToApiInput(
        validatedData,
        semenId,
      );
      const saveSemen = isBlockInactiveSiresEnabled ? editSemenV2 : editSemen;
      const results = await saveSemen([transformedInput]);

      handleApiResults(results, 'Semen updated successfully');

      if (results.every((r) => r.success)) {
        onSuccess?.(validatedData);
        reset();
        setSelectedSemen(null);
        onCancel();
      }
    } catch (error) {
      console.error('Failed to update semen:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update semen',
          life: ToastLife.MEDIUM,
        }),
      );
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    setSelectedSemen(null);
    setFormValues(null);
    onCancel();
  };

  const handleBulkEditSubmit = async (
    formValues: EditSemenFormInput,
    rows: SemenDto[],
  ) => {
    const validatedData = editSemenFormSchema.parse(formValues);
    const editInputs: EditSemenInput[] = rows.map((row) =>
      transformFormDataToApiInput(validatedData, row.id),
    );
    const saveSemen = isBlockInactiveSiresEnabled ? editSemenV2 : editSemen;
    return await saveSemen(editInputs);
  };

  const handleBulkEditSuccess = (validatedData: EditSemenFormValues) => {
    onSuccess?.(validatedData);
    reset();
    setFormValues(null);
  };

  const sexOptions = Object.entries(SEX).map(([label, value]) => ({
    label:
      isSemenConventionalTypeEnabled && value === SEX.Unknown
        ? 'Conventional'
        : label,
    value,
  }));
  const exportOptions = Object.entries(EXPORT_ENUMS).map(([label, value]) => ({
    label,
    value,
  }));
  const sortStatusOptions = [{ label: 'Rejected', value: 100000000 }];
  const criticalOptions = [{ label: 'Critical', value: 100000000 }];

  const renderFormContent = () => (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="bg-white p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-blue-600">
            {isBulkEdit
              ? 'Bulk Edit Semen Information'
              : 'Edit Semen Information'}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-1">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label
                  id="dateReceived-label"
                  className="text-sm font-medium text-gray-700"
                >
                  Date Received
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="dateReceived"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Calendar
                      id="dateReceived"
                      aria-labelledby="dateReceived-label"
                      value={field.value}
                      className={classNames('w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(e) => field.onChange(e.value)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      showOnFocus={false}
                      placeholder="10/7/2025"
                      container={calendarContainer}
                    />
                  )}
                />
                {getFormErrorMessage('dateReceived')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Sire
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="sire"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AdvancedSireLookup
                      value={field.value as Sire | undefined}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(sire) => field.onChange(sire ?? undefined)}
                      showAddButton={false}
                      disabled
                    />
                  )}
                />
                {getFormErrorMessage('sire')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Cane
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="cane"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="cane"
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('cane')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Ranch
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="owner"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AccountAutoComplete
                      id={field.name}
                      key={field.value?.id || 'owner'}
                      defaultValue={field.value as any}
                      defaultId={field.value?.id}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value);
                      }}
                      disabled
                    />
                  )}
                />
                {getFormErrorMessage('owner')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Sold To
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="soldTo"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="soldTo"
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('soldTo')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="colCode"
                  className="text-sm font-medium text-gray-700"
                >
                  Col Code
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="colCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      id="colCode"
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('colCode')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  NAAB
                </label>
                {(!isConditionalValidation || isNaabRequired) && (
                  <span className="text-red-500">*</span>
                )}
              </div>
              <div className="w-64">
                <Controller
                  name="naab"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="naab"
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('naab')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Received From
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="center"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="center"
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('center')}
              </div>
            </div>

            {!isSemenTypeLabelEnabled && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Sexed
                  </label>
                  <span className="text-red-500">*</span>
                </div>
                <div className="w-64">
                  <Controller
                    name="sexed"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        id="sexed"
                        data-testid="sexed"
                        value={field.value}
                        options={sexedOptions}
                        className="w-full text-sm"
                        onChange={(e) => field.onChange(e.value)}
                        placeholder="Select Sexed Type"
                      />
                    )}
                  />
                  {getFormErrorMessage('sexed')}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Export
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="export"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      inputId={field.name}
                      data-testid="export"
                      value={field.value}
                      options={exportOptions}
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="Select export status"
                      className="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('export')}
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  CSS Stamped
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="cssCertified"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      inputId={field.name}
                      data-testid="cssCertified"
                      value={field.value}
                      options={cssCertifiedOptions}
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="Select CSS Stamped status"
                      className="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('cssCertified')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Sort Status
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="sortStatus"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="sortStatus"
                      data-testid="sortStatus"
                      value={field.value}
                      options={sortStatusOptions}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="Select Sort Status"
                    />
                  )}
                />
                {getFormErrorMessage('sortStatus')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Semen Comment
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="comment"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="comment"
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('comment')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Critical
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="critical"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="critical"
                      data-testid="critical"
                      value={field.value}
                      options={criticalOptions}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder="Select Critical"
                    />
                  )}
                />
                {getFormErrorMessage('critical')}
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Storage Start Date
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="storageStartDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="storageStartDate"
                      value={field.value}
                      className="w-full"
                      onChange={(e) => field.onChange(e.value)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      showOnFocus={false}
                      placeholder="Select date"
                      container={calendarContainer}
                    />
                  )}
                />
                {getFormErrorMessage('storageStartDate')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Secondary Owner
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="secondaryOwner"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="secondaryOwner"
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('secondaryOwner')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {isSemenTypeLabelEnabled ? 'Semen Type' : 'Sex'}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="sex"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="sex"
                      data-testid="sex"
                      value={field.value}
                      options={sexOptions}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder={
                        isSemenTypeLabelEnabled
                          ? 'Select Semen Type'
                          : 'Select Sex Type'
                      }
                    />
                  )}
                />
                {getFormErrorMessage('sex')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Cost
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="cost"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      id="cost"
                      value={field.value ?? null}
                      className="w-full"
                      inputClassName="w-full text-sm"
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      showButtons
                      min={0}
                      mode="decimal"
                    />
                  )}
                />
                {getFormErrorMessage('cost')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Shipment
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="shipment"
                  control={control}
                  render={({ field, fieldState }) => (
                    <ShipmentAutoComplete
                      id={field.name}
                      key={field.value?.id || 'shipment'}
                      defaultValue={field.value as any}
                      defaultId={field.value?.id}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value);
                      }}
                    />
                  )}
                />
                {getFormErrorMessage('shipment')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Qty to Ship
                </label>
              </div>
              <div className="w-64">
                <Controller
                  name="quantityToShip"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      id="quantityToShip"
                      value={field.value ?? null}
                      className="w-full"
                      inputClassName="w-full text-sm"
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      showButtons
                      min={0}
                    />
                  )}
                />
                {getFormErrorMessage('quantityToShip')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Storage Location
                </label>
                <span className="text-red-500">*</span>
                <i className="pi pi-lock text-xs text-gray-400"></i>
              </div>
              <div className="w-64">
                <Controller
                  name="storageLocation"
                  control={control}
                  render={({ field }) => (
                    <>
                      <StorageLocationAutoComplete
                        defaultValue={field.value as any}
                        value={field.value as any}
                        field="name"
                        onSelect={(e: AutoCompleteSelectEvent) => {
                          handleStorageLocationSelect(e.value);
                          field.onChange(e.value);
                        }}
                        className="w-full"
                        disabled
                      />
                    </>
                  )}
                />
                {getFormErrorMessage('storageLocation')}
                {!isStorageLocationQualified && storageLocationTooltip && (
                  <small className="mt-1 text-sm text-gray-500">
                    {storageLocationTooltip}
                  </small>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  Quantity
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="quantity"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      id="quantity"
                      value={field.value ?? null}
                      className="w-full"
                      inputClassName="w-full text-sm"
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      showButtons
                      min={0}
                      disabled
                    />
                  )}
                />
                {getFormErrorMessage('quantity')}
              </div>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="quantityReceived"
                  className="text-sm font-medium text-gray-700"
                >
                  Qty Received
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-64">
                <Controller
                  name="quantityReceived"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputNumber
                      id="quantityReceived"
                      value={field.value ?? null}
                      className={classNames('w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      inputClassName="w-full text-sm"
                      onValueChange={(e) =>
                        field.onChange(e.value ?? undefined)
                      }
                      showButtons
                      min={0}
                    />
                  )}
                />
                {getFormErrorMessage('quantityReceived')}
              </div>
            </div>

            {isSemenDataPointsEnabled && (
              <>
                {!isSemenTypeLabelEnabled && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Semen Type
                      </label>
                    </div>
                    <div className="w-64">
                      <Controller
                        name="semenType"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            id="semenType"
                            value={field.value}
                            options={SemenTypeOptions}
                            optionLabel="label"
                            optionValue="value"
                            className="w-full text-sm"
                            onChange={(e) => field.onChange(e.value)}
                            placeholder="Select Semen Type"
                          />
                        )}
                      />
                      {getFormErrorMessage('semenType')}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Units
                    </label>
                  </div>
                  <div className="w-64">
                    <Controller
                      name="units"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          id="units"
                          value={field.value ?? null}
                          className="w-full"
                          inputClassName="w-full text-sm"
                          onValueChange={(e) =>
                            field.onChange(e.value ?? undefined)
                          }
                          showButtons
                          min={0}
                        />
                      )}
                    />
                    {getFormErrorMessage('units')}
                  </div>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Export Country
                    </label>
                  </div>
                  <div className="w-64">
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <InputText
                          id="country"
                          value={field.value || ''}
                          className="w-full text-sm"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                    {getFormErrorMessage('country')}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {!isBulkEdit && (
        <div className="flex justify-end gap-2 pt-6">
          <Button
            type="button"
            label={t('common.cancel')}
            icon="pi pi-times"
            severity="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Cancel edit semen"
          />
          <Button
            type="submit"
            label={t('common.save')}
            icon="pi pi-check"
            loading={isSubmitting}
          />
        </div>
      )}
    </form>
  );

  const renderBaseUiFormContent = () => {
    const renderField = ({
      name,
      label,
      required,
      locked,
      children,
    }: {
      name: keyof EditSemenFormInput;
      label: string;
      required?: boolean;
      locked?: boolean;
      children: React.ReactNode;
    }) => (
      <Field data-invalid={!!errors[name]}>
        <FieldLabel className="font-normal" htmlFor={name}>
          {label}
          {required && <span className="text-destructive">*</span>}
          {locked && <LockIcon className="text-muted-foreground size-3" />}
        </FieldLabel>
        {children}
        <FieldError
          errors={[errors[name] as { message?: string } | undefined]}
        />
      </Field>
    );

    const renderTextField = (
      name:
        | 'cane'
        | 'soldTo'
        | 'colCode'
        | 'naab'
        | 'center'
        | 'comment'
        | 'secondaryOwner'
        | 'country',
      label: string,
      required?: boolean,
    ) =>
      renderField({
        name,
        label,
        required,
        children: (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Input
                id={name}
                value={field.value || ''}
                aria-invalid={!!errors[name]}
                onChange={(e) => field.onChange(e.target.value)}
              />
            )}
          />
        ),
      });

    const renderSelectField = <Value extends string | number>(
      name:
        | 'sexed'
        | 'export'
        | 'cssCertified'
        | 'sortStatus'
        | 'critical'
        | 'sex'
        | 'semenType',
      label: string,
      options: readonly { label: string; value: Value }[],
      placeholder: string,
      required?: boolean,
    ) =>
      renderField({
        name,
        label,
        required,
        children: (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Select
                value={(field.value as Value | undefined) ?? null}
                onChange={(value) => field.onChange(value ?? undefined)}
                items={options}
              >
                <SelectTrigger
                  id={name}
                  data-testid={name}
                  className="w-full"
                  aria-invalid={!!errors[name]}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent
                  alignItemWithTrigger={false}
                  container={calendarContainer}
                >
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        ),
      });

    const renderNumberField = (
      name:
        | 'cost'
        | 'quantityToShip'
        | 'quantity'
        | 'quantityReceived'
        | 'units',
      label: string,
      options: { required?: boolean; disabled?: boolean; step?: string } = {},
    ) =>
      renderField({
        name,
        label,
        required: options.required,
        children: (
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <Input
                id={name}
                type="number"
                min={0}
                step={options.step ?? '1'}
                disabled={options.disabled}
                value={field.value ?? ''}
                aria-invalid={!!errors[name]}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
              />
            )}
          />
        ),
      });

    return (
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6 bg-white p-6"
      >
        <h3 className="text-primary text-base font-semibold">
          {isBulkEdit
            ? t('semen.bulkEditSemenInformation')
            : t('semen.editSemenInformation')}
        </h3>
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-3">
          {renderField({
            name: 'dateReceived',
            label: t('semen.dateReceived'),
            required: true,
            children: (
              <Controller
                name="dateReceived"
                control={control}
                render={({ field }) => (
                  <DatePickerInput
                    id="dateReceived"
                    key={semen?.id ?? 'dateReceived'}
                    className={DATE_PICKER_COMPACT}
                    initialDate={field.value}
                    container={calendarContainer}
                    onChange={(date) => field.onChange(date ?? undefined)}
                  />
                )}
              />
            ),
          })}
          {renderField({
            name: 'sire',
            label: t('semen.sire'),
            required: true,
            children: (
              <Controller
                name="sire"
                control={control}
                render={({ field, fieldState }) => (
                  <AdvancedSireLookup
                    value={field.value as Sire | undefined}
                    className={classNames('w-full text-sm', {
                      'p-invalid': fieldState.invalid,
                    })}
                    onChange={(sire) => field.onChange(sire ?? undefined)}
                    showAddButton={false}
                    disabled
                  />
                )}
              />
            ),
          })}
          {renderTextField('cane', t('semen.cane'), true)}
          {renderField({
            name: 'owner',
            label: t('common.ranch'),
            required: true,
            children: (
              <Controller
                name="owner"
                control={control}
                render={({ field }) => (
                  <AccountsCombobox
                    className="w-full"
                    container={calendarContainer}
                    value={field.value?.id}
                    onChange={(accountId) =>
                      field.onChange(
                        accountId ? { id: accountId, name: '' } : undefined,
                      )
                    }
                    disabled
                  />
                )}
              />
            ),
          })}
          {renderTextField('soldTo', t('semen.soldTo'))}
          {renderTextField('colCode', t('semen.colCode'), true)}
          {renderTextField(
            'naab',
            t('semen.naab'),
            !isConditionalValidation || isNaabRequired,
          )}
          {renderTextField('center', t('semen.receivedFrom'))}
          {!isSemenTypeLabelEnabled &&
            renderSelectField(
              'sexed',
              t('semen.sexed'),
              sexedOptions,
              t('semen.selectSexedType'),
              true,
            )}
          {renderSelectField(
            'export',
            t('semen.export'),
            exportOptions,
            t('semen.selectExportStatus'),
          )}
          {renderSelectField(
            'cssCertified',
            t('semen.css'),
            cssCertifiedOptions,
            t('semen.selectCssStampedStatus'),
            true,
          )}
          {renderSelectField(
            'sortStatus',
            t('semen.sortStatus'),
            sortStatusOptions,
            t('semen.selectSortStatus'),
          )}
          {renderTextField('comment', t('semen.semenComment'))}
          {renderSelectField(
            'critical',
            t('semen.critical'),
            criticalOptions,
            t('semen.selectCritical'),
          )}
          {renderField({
            name: 'storageStartDate',
            label: t('semen.storageStartDate'),
            required: true,
            children: (
              <Controller
                name="storageStartDate"
                control={control}
                render={({ field }) => (
                  <DatePickerInput
                    id="storageStartDate"
                    key={semen?.id ?? 'storageStartDate'}
                    className={DATE_PICKER_COMPACT}
                    initialDate={field.value}
                    container={calendarContainer}
                    placeholder={t('semen.selectDate')}
                    onChange={(date) => field.onChange(date ?? undefined)}
                  />
                )}
              />
            ),
          })}
          {renderTextField('secondaryOwner', t('semen.secondaryOwner'))}
          {renderSelectField(
            'sex',
            isSemenTypeLabelEnabled
              ? t('semen.semenTypeFieldLabel')
              : t('semen.sex'),
            sexOptions,
            isSemenTypeLabelEnabled
              ? t('semen.selectSemenTypeFieldLabel')
              : t('semen.selectSexType'),
            true,
          )}
          {renderNumberField('cost', t('semen.cost'), { step: 'any' })}
          {renderField({
            name: 'shipment',
            label: t('semen.shipment'),
            children: (
              <Controller
                name="shipment"
                control={control}
                render={({ field, fieldState }) => (
                  <ShipmentAutoComplete
                    id={field.name}
                    key={field.value?.id || 'shipment'}
                    defaultValue={field.value as any}
                    defaultId={field.value?.id}
                    className={classNames('w-full text-sm', {
                      'p-invalid': fieldState.invalid,
                    })}
                    onSelect={(e: AutoCompleteSelectEvent) => {
                      field.onChange(e.value);
                    }}
                  />
                )}
              />
            ),
          })}
          {renderNumberField('quantityToShip', t('semen.qtyToShip'))}
          {renderField({
            name: 'storageLocation',
            label: t('storageLocation.storageLocation'),
            required: true,
            locked: true,
            children: (
              <>
                <Controller
                  name="storageLocation"
                  control={control}
                  render={({ field }) => (
                    <StorageLocationCombobox
                      value={
                        (field.value as
                          | { id: string; name: string }
                          | undefined) ?? null
                      }
                      onChange={(location) => {
                        handleStorageLocationSelect(location);
                        field.onChange(location ?? undefined);
                      }}
                      pagingEnabled
                      placeholder=""
                      container={calendarContainer}
                      contentClassName="w-max"
                      disabled
                    />
                  )}
                />
                {!isStorageLocationQualified && storageLocationTooltip && (
                  <small className="text-muted-foreground text-sm">
                    {storageLocationTooltip}
                  </small>
                )}
              </>
            ),
          })}
          {renderNumberField('quantity', t('semen.quantity'), {
            required: true,
            disabled: true,
          })}
          {renderNumberField('quantityReceived', t('semen.qtyReceived'), {
            required: true,
          })}
          {isSemenDataPointsEnabled && (
            <>
              {!isSemenTypeLabelEnabled &&
                renderSelectField(
                  'semenType',
                  t('semen.semenTypeFieldLabel'),
                  SemenTypeOptions as { label: string; value: string }[],
                  t('semen.selectSemenTypeFieldLabel'),
                )}
              {renderNumberField('units', t('semen.units'))}
              {renderTextField('country', t('semen.exportCountry'))}
            </>
          )}
        </div>
        {!isBulkEdit && (
          <div className="flex justify-end gap-2">
            <UiButton
              variant="outline"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              aria-label={t('semen.cancelEditSemen')}
            >
              {t('common.cancel')}
            </UiButton>
            <UiButton type="submit" disabled={isSubmitting}>
              {t('common.save')}
            </UiButton>
          </div>
        )}
      </form>
    );
  };

  if (isBulkEdit) {
    return (
      <MassEditWizard
        rows={rows}
        getFormValues={() => formValues}
        validateForm={trigger}
        config={derivedMassEditSemenConfig}
        onSubmit={handleBulkEditSubmit}
        onSuccess={handleBulkEditSuccess}
        onError={onError}
        onCancel={handleCancel}
        title="Bulk Edit Semen Wizard"
        isSubmitting={isSubmitting}
        themeMode={themeMode}
      >
        {isBaseUi ? renderBaseUiFormContent() : renderFormContent()}
      </MassEditWizard>
    );
  }

  return isBaseUi ? renderBaseUiFormContent() : renderFormContent();
};
